import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clientRoutes } from "@/lib/clientRoutes";
import { RECORD_TYPE_LABEL } from "@/lib/client360Labels";
import type { Client360Envelope } from "@/api/clients";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Dog,
  Lock,
  Loader2,
  ShieldCheck,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { ApiClientError } from "@/api/client";
import {
  fetchBrands,
  fetchBreeds,
  fetchBusinessProfile,
  quickFormSave,
  type BrandRef,
  type BreedRef,
  type GateBranch,
  type QuickFormSavePayload,
} from "@/api/businessProfile";

/* ── option labels (Ukrainian) ─────────────────────────────────────────── */
export const BUSINESS_MODELS: Array<[string, string]> = [
  ["independent_pet_shop", "Зоомагазин (1 ТТ)"],
  ["retail_chain", "Мережа"],
  ["vet_clinic", "Ветклініка"],
  ["grooming_salon", "Грумінг"],
  ["breeder_professional", "Заводчик"],
  ["online_store", "Інтернет-магазин"],
  ["dealer_partner", "Дилер / представник"],
  ["marketplace_seller", "Маркетплейс"],
  ["other", "Інше"],
];
export const M_LABEL = Object.fromEntries(BUSINESS_MODELS);

export const SCALE: Array<[string, string]> = [
  ["micro_1pt", "Мікро (1 ТТ)"],
  ["small_2_3pts", "Малий (2–3 ТТ)"],
  ["medium_4_10pts", "Середній (4–10 ТТ)"],
  ["large_11plus", "Великий (11+)"],
  ["strategic_chain", "Стратегічна мережа"],
];
export const CHANNEL: Array<[string, string]> = [
  ["pet_retail", "Зоороздріб"],
  ["retail_chain", "Мережа"],
  ["vet_channel", "Вет-канал"],
  ["grooming_channel", "Грумінг"],
  ["ecommerce", "E-commerce"],
  ["wholesale", "Опт"],
  ["breeder_professional", "Заводчик"],
  ["mixed", "Змішаний"],
];
export const PURCHASING: Array<[string, string]> = [
  ["owner_buys", "Власник закуповує"],
  ["dedicated_buyer", "Окремий закупник"],
  ["centralized_procurement", "Централізовано"],
  ["outlet_level_purchasing", "На рівні ТТ"],
  ["accountant_gatekeeper", "Через бухгалтера"],
  ["mixed", "Змішано"],
  ["unknown", "Не уточнено"],
];
export const DECISION: Array<[string, string]> = [
  ["owner_led", "Власник вирішує"],
  ["buyer_led", "Закупник вирішує"],
  ["corporate_procurement", "Корпоративно"],
  ["family_business", "Сімейний бізнес"],
  ["franchise_rules", "Франшиза"],
  ["vet_specialist_influenced", "Вплив ветеринара"],
  ["unknown", "Не уточнено"],
];
export const ORDER_FREQ: Array<[string, string]> = [
  ["weekly", "Щотижня"],
  ["biweekly", "Раз на 2 тижні"],
  ["monthly", "Щомісяця"],
  ["irregular", "Нерегулярно"],
  ["seasonal", "Сезонно"],
  ["unknown", "Не уточнено"],
];
export const COUNT_BAND: Array<[string, string]> = [
  ["1_5", "1–5"],
  ["6_10", "6–10"],
  ["11_20", "11–20"],
  ["21_50", "21–50"],
  ["50_plus", "50+"],
];
export const ANIMAL_TYPES: Array<[string, string]> = [
  ["dogs", "Собаки"],
  ["cats", "Коти"],
];
/* ── 3.0A-4 scenario-block options ─────────────────────────────────────── */
export const NETWORK_TYPE: Array<[string, string]> = [
  ["regional", "Регіональна"],
  ["national", "Національна"],
];
export const DEALER_TYPE: Array<[string, string]> = [
  ["regional_representative", "Регіональний представник"],
  ["category_representative", "Представник категорії"],
  ["brand_representative", "Представник бренду"],
  ["mixed_representative", "Змішане представництво"],
  ["other", "Інше"],
];
export const EXCLUSIVITY: Array<[string, string]> = [
  ["exclusive", "Ексклюзивний"],
  ["non_exclusive", "Неексклюзивний"],
  ["unknown", "Не уточнено"],
];
export const SALES_CHANNELS: Array<[string, string]> = [
  ["own_site", "Власний сайт"],
  ["rozetka_marketplace", "Rozetka / маркетплейс"],
  ["instagram_fb", "Instagram / Facebook"],
  ["prom_olx", "Prom / OLX"],
  ["telegram", "Telegram"],
  ["combined", "Комбіновано"],
];
export const PRICE_POSITIONING: Array<[string, string]> = [
  ["budget", "Бюджет"],
  ["mid", "Середній"],
  ["premium", "Преміум"],
  ["dumping", "Демпінг"],
  ["unknown", "Не уточнено"],
];
export const ASSORTMENT: Array<[string, string]> = [
  ["dog_food", "Корм для собак"],
  ["cat_food", "Корм для котів"],
  ["vet_diets", "Ветдієти"],
  ["treats", "Ласощі"],
  ["accessories", "Аксесуари"],
  ["grooming_products", "Грумінг-товари"],
  ["hygiene", "Гігієна"],
  ["aquarium", "Акваріум"],
  ["small_pets", "Дрібні тварини"],
  ["breeder_products", "Для заводчиків"],
  ["premium_segment", "Преміум-сегмент"],
  ["economy_segment", "Економ-сегмент"],
];
export const OBLAST: string[] = [
  "Вінницька", "Волинська", "Дніпропетровська", "Донецька",
  "Житомирська", "Закарпатська", "Запорізька", "Івано-Франківська",
  "Київська", "Кіровоградська", "Луганська", "Львівська",
  "Миколаївська", "Одеська", "Полтавська", "Рівненська",
  "Сумська", "Тернопільська", "Харківська", "Херсонська",
  "Хмельницька", "Черкаська", "Чернівецька", "Чернігівська",
  "АР Крим", "Київ", "Севастополь",
];

export function asArray(v: unknown): string[] {
  return Array.isArray(v) ? (v as string[]) : [];
}
export function asStr(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/**
 * A-Block A3 — BusinessProfile Gate v2 Quick Form (holding-grain, type-aware).
 *
 * cCPrimaryBusinessModel drives the gate BRANCH (independent of the card
 * variant): breeder_professional → breeder branch (animalTypes + breeds/other +
 * countBand); else commercial branch (scale + purchasing + decision +
 * channel/override). soft-save never blocks; hard-gate only on mark-reviewed.
 * Buttons gated by backend allowedActions.
 */
export function BusinessProfilePage() {
  const { holdingId } = useParams<{ holdingId: string }>();
  const id = holdingId ?? "";
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Classification context (grain-clean v2.1): recordType + display name come from
  // the Client360 envelope. Profile shows them read-only and routes model edits to
  // the «Класифікація» drawer — the profile never owns the primaryBusinessModel axis.
  const env = useOutletContext<Client360Envelope | undefined>();

  const q = useQuery({
    queryKey: ["businessProfile", id],
    queryFn: ({ signal }) => fetchBusinessProfile(id, signal),
    enabled: !!id,
    staleTime: 60_000,
  });

  // Editable local state, seeded from backend.
  const [models, setModels] = useState<string[]>([]);
  const [primary, setPrimary] = useState("");
  const [otherNote, setOtherNote] = useState("");
  const [channel, setChannel] = useState("");
  const [override, setOverride] = useState("");
  const [scale, setScale] = useState("");
  const [purchasing, setPurchasing] = useState("");
  const [decision, setDecision] = useState("");
  const [orderFreq, setOrderFreq] = useState("");
  const [animalTypes, setAnimalTypes] = useState<string[]>([]);
  const [breeds, setBreeds] = useState<BreedRef[]>([]);
  const [breedOther, setBreedOther] = useState("");
  const [countBand, setCountBand] = useState("");
  // 3.0A-4 scenario blocks
  const [networkType, setNetworkType] = useState("");
  const [networkOverride, setNetworkOverride] = useState("");
  const [dealerType, setDealerType] = useState("");
  const [dealerTerritory, setDealerTerritory] = useState<string[]>([]);
  const [representedBrands, setRepresentedBrands] = useState<BrandRef[]>([]);
  const [exclusivity, setExclusivity] = useState("");
  const [ecomShopName, setEcomShopName] = useState("");
  const [salesChannels, setSalesChannels] = useState<string[]>([]);
  const [pricePositioning, setPricePositioning] = useState("");
  const [ecomAssortment, setEcomAssortment] = useState<string[]>([]);
  const [activeOutlets, setActiveOutlets] = useState(0);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  useEffect(() => {
    const f = q.data?.fields;
    if (!f) return;
    setModels(asArray(f.cCBusinessModels));
    setPrimary(asStr(f.cCPrimaryBusinessModel));
    setOtherNote(asStr(f.cCBusinessModelOtherNote));
    setChannel(asStr(f.cCPrimaryChannel));
    setOverride(asStr(f.cCManagerReviewOverrideReason));
    setScale(asStr(f.cCBusinessScale));
    setPurchasing(asStr(f.cCPurchasingModel));
    setDecision(asStr(f.cCDecisionModel));
    setOrderFreq(asStr(f.cCOrderFrequency));
    setAnimalTypes(asArray(f.cCBreederAnimalTypes));
    setBreeds(Array.isArray(f.cCBreederBreeds) ? (f.cCBreederBreeds as BreedRef[]) : []);
    setBreedOther(asStr(f.cCBreederBreedOtherText));
    setCountBand(asStr(f.cCBreederAnimalCountBand));
    setNetworkType(asStr(f.cCNetworkType));
    setNetworkOverride(asStr(f.cCNetworkSizeOverrideReason));
    setDealerType(asStr(f.cCDealerType));
    setDealerTerritory(asArray(f.cCDealerTerritoryRegions));
    setRepresentedBrands(Array.isArray(f.cCRepresentedKormaxBrands) ? (f.cCRepresentedKormaxBrands as BrandRef[]) : []);
    setExclusivity(asStr(f.cCExclusivityStatus));
    setEcomShopName(asStr(f.cCEcomShopName));
    setSalesChannels(asArray(f.cCSalesChannels));
    setPricePositioning(asStr(f.cCPricePositioning));
    setEcomAssortment(asArray(f.cCEcomAssortmentFocus));
    setActiveOutlets(typeof f.cCActiveOutletsCount === "number" ? (f.cCActiveOutletsCount as number) : 0);
  }, [q.data]);

  const branch: GateBranch = primary === "breeder_professional" ? "breeder" : "commercial_non_breeder";

  // Client-side gate preview — MIRRORS backend getMissingRequiredFields exactly
  // (incl. cCOrderFrequency + cCBusinessModelOtherNote for parity).
  const missing = useMemo(() => {
    const m: string[] = [];
    if (models.length === 0) m.push("Бізнес-моделі");
    if (!primary || (primary && !models.includes(primary))) m.push("Основна бізнес-модель");
    if (primary === "other" && otherNote.trim().length < 3) m.push("Опис іншої моделі");
    if (branch === "breeder") {
      if (animalTypes.length === 0) m.push("Види тварин");
      if (breeds.length === 0 && breedOther.trim().length < 2) m.push("Породи (зі списку або текстом)");
      if (!countBand) m.push("Орієнтовна кількість тварин");
    } else {
      if (!channel && !override) m.push("Основний канал");
      if (!scale) m.push("Масштаб бізнесу");
      if (!purchasing) m.push("Модель закупівель");
      if (!decision) m.push("Модель прийняття рішень");
      if (!orderFreq) m.push("Частота замовлень");
    }
    return m;
  }, [models, primary, otherNote, branch, animalTypes, breeds, breedOther, countBand, channel, override, scale, purchasing, decision, orderFreq]);

  // Total matches backend getRequiredTotal: breeder=5, commercial=7 (8 if primary='other').
  const reviewed = q.data?.status === "manager_reviewed";

  // 3.0A-4 type-conditional gate preview — mirrors backend
  // getMissingTypeConditionalFields. Holding-grain: chain/dealer/ecommerce.
  const typeMissing = useMemo(() => {
    const t: string[] = [];
    if (models.includes("retail_chain") && activeOutlets < 3 && networkOverride.trim() === "") {
      t.push("Причина (мережа < 3 точок)");
    }
    if (models.includes("dealer_partner")) {
      if (!dealerType) t.push("Тип представництва");
      if (representedBrands.length === 0) t.push("Представлені бренди");
    }
    if (models.includes("online_store")) {
      if (salesChannels.length === 0 && !pricePositioning && ecomAssortment.length === 0) {
        t.push("E-commerce деталі (канал/ціна/асортимент)");
      }
    }
    return t;
  }, [models, activeOutlets, networkOverride, dealerType, representedBrands, salesChannels, pricePositioning, ecomAssortment]);

  // GRANDFATHER (Condition C): already-reviewed profiles are NOT re-gated on the
  // new type-conditional axes — no false "missing" in the preview, mirroring the
  // backend (Hook #6 enforces type-conditional only on the first reviewed transition).
  const effectiveTypeMissing = reviewed ? [] : typeMissing;

  const total = branch === "breeder" ? 5 : primary === "other" ? 8 : 7;
  const filled = Math.max(0, total - missing.length);
  const percent = total > 0 ? Math.round((filled / total) * 100) : 0;
  const canReview = missing.length === 0 && effectiveTypeMissing.length === 0;

  const aa = q.data?.allowedActions;
  const profileId = q.data?.profileId ?? null;
  const versionNumber = q.data?.versionNumber ?? null;

  // ── save wiring (GATE 3a) — real persist → invalidate → reload shows saved ──
  function buildStepData(): QuickFormSavePayload["stepData"] {
    return {
      step1: {
        cCBusinessModels: models,
        cCPrimaryBusinessModel: primary,
        cCBusinessModelOtherNote: otherNote,
        cCPrimaryChannel: channel,
        cCManagerReviewOverrideReason: override,
      },
      step2: {
        cCBusinessScale: scale,
        cCPurchasingModel: purchasing,
        cCDecisionModel: decision,
        cCOrderFrequency: orderFreq,
        cCBreederAnimalTypes: animalTypes,
        cCBreederBreeds: breeds,
        cCBreederBreedOtherText: breedOther,
        cCBreederAnimalCountBand: countBand,
        // 3.0A-4 scenario blocks
        cCNetworkType: networkType,
        cCNetworkSizeOverrideReason: networkOverride,
        cCDealerType: dealerType,
        cCDealerTerritoryRegions: dealerTerritory,
        cCRepresentedKormaxBrands: representedBrands.map((b) => ({ biBrandId: b.biBrandId, name: b.name })),
        cCExclusivityStatus: exclusivity,
        cCEcomShopName: ecomShopName,
        cCSalesChannels: salesChannels,
        cCPricePositioning: pricePositioning,
        cCEcomAssortmentFocus: ecomAssortment,
      },
    };
  }

  const saveMutation = useMutation({
    mutationFn: (action: QuickFormSavePayload["action"]) => {
      if (!profileId || versionNumber == null) {
        return Promise.reject(new Error("Профіль не створено — збереження недоступне"));
      }
      return quickFormSave(profileId, {
        versionNumber,
        stepData: buildStepData(),
        action,
      });
    },
    onSuccess: async (_res, action) => {
      setToast({
        kind: "ok",
        msg: action === "save_and_mark_reviewed" ? "Збережено та позначено перевіреним" : "Чернетку збережено",
      });
      await queryClient.invalidateQueries({ queryKey: ["businessProfile", id] });
    },
    onError: (e: unknown) => {
      if (e instanceof ApiClientError && (e.status === 409 || e.code === "profile_conflict")) {
        setToast({ kind: "err", msg: "Профіль змінено іншим користувачем — форму оновлено. Повторіть." });
        void queryClient.invalidateQueries({ queryKey: ["businessProfile", id] });
        return;
      }
      const msg = e instanceof Error ? e.message : "Помилка збереження";
      setToast({ kind: "err", msg });
    },
  });
  const saving = saveMutation.isPending;

  if (q.isPending) {
    return <div className="p-6 md:p-8"><div className="h-40 animate-pulse rounded-lg bg-muted" /></div>;
  }
  if (q.isError || !q.data) {
    return (
      <div className="p-6 md:p-8 text-center">
        <AlertTriangle className="mx-auto mb-2 h-6 w-6 text-destructive" />
        <div className="text-sm font-medium">Не вдалося завантажити бізнес-профіль</div>
        <button onClick={() => q.refetch()} className="mt-3 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground">Повторити</button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6 md:p-8">
      {/* Status header — AC-2: type-aware suffix (breeder/single ≠ «холдинг») */}
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold tracking-tight">
          Бізнес-профіль
          {(env?.header.activeMembers ?? 0) > 1
            ? " · холдинг"
            : (env?.header.recordType === "breeder_person" || branch === "breeder") ? " · заводчик" : ""}
        </h2>
        {reviewed ? (
          <span className="flex items-center gap-1.5 text-sm font-medium text-success-foreground"><ShieldCheck className="h-4 w-4" /> Перевірено</span>
        ) : (
          <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground"><Clock className="h-4 w-4" /> Не перевірено</span>
        )}
      </div>

      {/* ── Classification summary (grain-clean v2.1, read-only) ──────────────
          Тип запису + основна модель live in «Класифікація», not the profile. */}
      <div className="rounded-xl border bg-card px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Тип запису</div>
            <div className="font-medium">{RECORD_TYPE_LABEL[env?.header.recordType ?? "unknown"] ?? "Не визначено"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Основна модель</div>
            <div className="font-medium">{primary ? (M_LABEL[primary] ?? primary) : "—"}</div>
          </div>
          <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", branch === "breeder" ? "bg-primary/10 text-primary" : "bg-muted text-foreground")}>
            гілка: {branch === "breeder" ? "Заводчик" : "Комерційний"}
          </span>
          <button
            type="button"
            onClick={() => navigate(clientRoutes.client(id))}
            className="ml-auto text-xs font-semibold text-primary hover:underline"
          >
            Редагувати класифікацію →
          </button>
        </div>
        <p className="mt-1.5 text-[10.5px] text-muted-foreground/80">
          ★ Основну модель змінюють у «Класифікація», не тут. Тут — як клієнт працює
          (бізнес-характеристики). Юр. дані → «Юрособи». Адреси / точки → «Точки».
        </p>
      </div>

      {!q.data.exists && (
        <div className="rounded-lg border border-warning-foreground/20 bg-warning px-3 py-2 text-sm text-warning-foreground">
          Профіль для цього холдингу ще не створено — заповніть і збережіть, щоб створити чернетку.
        </div>
      )}

      {/* ── Common ─────────────────────────────────────────────── */}
      <Card title="Бізнес-моделі" hint="Визначає гілку перевірки (заводчик / комерційний)">
        <div className="flex flex-wrap gap-1.5">
          {BUSINESS_MODELS.map(([k, label]) => (
            <Chip key={k} on={models.includes(k)} onClick={() => setModels((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k])}>
              {label}
            </Chip>
          ))}
        </div>
        <Field label="Основна модель (драйвить гілку)" req className="mt-3">
          <Select value={primary} onChange={setPrimary} options={[["", "—"], ...models.map((m): [string, string] => [m, M_LABEL[m] ?? m])]} />
        </Field>
        {primary === "other" && (
          <Field label="Опис іншої моделі" req hint="Мінімум 3 символи" className="mt-3">
            <input value={otherNote} onChange={(e) => setOtherNote(e.target.value)} maxLength={500} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" placeholder="Опишіть бізнес-модель…" />
          </Field>
        )}
      </Card>

      {/* ── Branch indicator ───────────────────────────────────── */}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Гілка перевірки:</span>
        <span className={cn("rounded-full px-2 py-0.5 font-medium", branch === "breeder" ? "bg-primary/10 text-primary" : "bg-muted text-foreground")}>
          {branch === "breeder" ? "Заводчик" : "Комерційний"}
        </span>
      </div>

      {/* ── Commercial branch ──────────────────────────────────── */}
      {branch === "commercial_non_breeder" && (
        <Card title="Комерційні параметри" hint="Потрібні для перевірки комерційного клієнта">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Масштаб бізнесу" req><Select value={scale} onChange={setScale} options={[["", "—"], ...SCALE]} /></Field>
            <Field label="Модель закупівель" req><Select value={purchasing} onChange={setPurchasing} options={[["", "—"], ...PURCHASING]} /></Field>
            <Field label="Модель рішень" req><Select value={decision} onChange={setDecision} options={[["", "—"], ...DECISION]} /></Field>
            <Field label="Основний канал" req><Select value={channel} onChange={setChannel} options={[["", "—"], ...CHANNEL]} /></Field>
            <Field label="Частота замовлень" req><Select value={orderFreq} onChange={setOrderFreq} options={[["", "—"], ...ORDER_FREQ]} /></Field>
          </div>
          <Field label="Або причина-override (якщо каналу немає)" className="mt-3">
            <input value={override} onChange={(e) => setOverride(e.target.value)} placeholder="Напр.: канал не застосовний" className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
          </Field>
        </Card>
      )}

      {/* ── Breeder branch ─────────────────────────────────────── */}
      {branch === "breeder" && (
        <Card title="Профіль заводчика" hint="Лише собаки / коти (LOCKED)">
          <Field label="Види тварин" req>
            <div className="flex gap-1.5">
              {ANIMAL_TYPES.map(([k, label]) => (
                <Chip key={k} on={animalTypes.includes(k)} onClick={() => setAnimalTypes((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k])}>
                  <Dog className="mr-1 inline h-3 w-3" />{label}
                </Chip>
              ))}
            </div>
          </Field>

          <BreedsPicker animalTypes={animalTypes} selected={breeds} onChange={setBreeds} />

          <Field label="Порода поза довідником (собаки/кота)" hint="Якщо породи немає у списку. Мін. 2 символи. Не інший вид, не AI." className="mt-3">
            <input value={breedOther} onChange={(e) => setBreedOther(e.target.value)} placeholder="Напр.: рідкісна порода…" maxLength={255} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
          </Field>

          <Field label="Орієнтовна кількість тварин" req className="mt-3">
            <Select value={countBand} onChange={setCountBand} options={[["", "—"], ...COUNT_BAND]} />
          </Field>
        </Card>
      )}

      {/* ── 3.0A-4 scenario blocks (per selected business model) ── */}
      {models.includes("retail_chain") && (
        <Card title="Мережа" hint="Параметри роздрібної мережі">
          <Field label="Тип мережі">
            <Select value={networkType} onChange={setNetworkType} options={[["", "—"], ...NETWORK_TYPE]} />
          </Field>
          {activeOutlets < 3 && (
            <Field label="Причина (мережа < 3 точок)" req hint={`Активних точок: ${activeOutlets}. Потрібна для «Перевірено», якщо < 3.`} className="mt-3">
              <input value={networkOverride} onChange={(e) => setNetworkOverride(e.target.value)} placeholder="Напр.: відкриваються нові точки" className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
            </Field>
          )}
        </Card>
      )}

      {models.includes("dealer_partner") && (
        <Card title="Дилер / представник" hint="Тип, території та представлені бренди">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Тип представництва" req><Select value={dealerType} onChange={setDealerType} options={[["", "—"], ...DEALER_TYPE]} /></Field>
            <Field label="Ексклюзивність"><Select value={exclusivity} onChange={setExclusivity} options={[["", "—"], ...EXCLUSIVITY]} /></Field>
          </div>
          <Field label="Території представництва" className="mt-3">
            <ChipMulti options={OBLAST.map((o): [string, string] => [o, o])} selected={dealerTerritory} onToggle={(v) => setDealerTerritory((s) => s.includes(v) ? s.filter((x) => x !== v) : [...s, v])} scroll />
          </Field>
          <BrandsPicker selected={representedBrands} onChange={setRepresentedBrands} />
        </Card>
      )}

      {models.includes("online_store") && (
        <Card title="E-commerce" hint="Канал холдингу · URL береться з картки контрагента">
          <Field label="Назва інтернет-магазину">
            <input value={ecomShopName} onChange={(e) => setEcomShopName(e.target.value)} maxLength={255} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
          </Field>
          <Field label="Канали продажу" className="mt-3">
            <ChipMulti options={SALES_CHANNELS} selected={salesChannels} onToggle={(v) => setSalesChannels((s) => s.includes(v) ? s.filter((x) => x !== v) : [...s, v])} />
          </Field>
          <Field label="Цінове позиціювання" className="mt-3">
            <Select value={pricePositioning} onChange={setPricePositioning} options={[["", "—"], ...PRICE_POSITIONING]} />
          </Field>
          <Field label="Асортиментний фокус (онлайн)" className="mt-3">
            <ChipMulti options={ASSORTMENT} selected={ecomAssortment} onToggle={(v) => setEcomAssortment((s) => s.includes(v) ? s.filter((x) => x !== v) : [...s, v])} scroll />
          </Field>
        </Card>
      )}

      {/* ── Completeness + gate ────────────────────────────────── */}
      <Card title="Заповненість профілю">
        <div className="flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${percent}%` }} />
          </div>
          <span className="text-sm font-medium tabular-nums">{filled}/{total}</span>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Заповнення ніколи не блокує збереження. Жорсткий гейт — лише на «Позначити перевіреним».
        </p>
        {(() => {
          const allMissing = [...missing, ...effectiveTypeMissing];
          return allMissing.length > 0 ? (
            <div className="mt-3 space-y-1">
              <div className="text-xs font-medium text-warning-foreground">Перед «Перевірено» бракує:</div>
              {allMissing.slice(0, 6).map((mi) => (
                <div key={mi} className="flex items-center gap-1.5 text-xs text-muted-foreground"><X className="h-3 w-3 text-warning-foreground" /> {mi}</div>
              ))}
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-success-foreground"><CheckCircle2 className="h-3.5 w-3.5" /> Готово до перевірки</div>
          );
        })()}
      </Card>

      {/* ── toast ──────────────────────────────────────────────── */}
      {toast && (
        <div
          role="status"
          className={cn(
            "rounded-lg border px-3 py-2 text-sm",
            toast.kind === "ok"
              ? "border-success-foreground/20 bg-success text-success-foreground"
              : "border-destructive/30 bg-destructive/10 text-destructive",
          )}
        >
          {toast.msg}
        </div>
      )}

      {/* ── Actions (gated by backend allowedActions) ──────────── */}
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => { setToast(null); saveMutation.mutate("save_draft"); }}
          disabled={!aa?.canSaveBusinessProfileDraft || saving || !profileId}
          className="flex items-center gap-1.5 rounded-md border bg-background px-3.5 py-2 text-sm font-medium hover:bg-accent disabled:opacity-40"
          title={!profileId ? "Профіль ще не створено" : !aa?.canSaveBusinessProfileDraft ? "Немає права зберігати" : undefined}
        >
          {saving && saveMutation.variables === "save_draft" && <Loader2 className="h-4 w-4 animate-spin" />}
          Зберегти чернетку
        </button>
        <button
          onClick={() => { setToast(null); saveMutation.mutate("save_and_mark_reviewed"); }}
          disabled={!aa?.canMarkBusinessProfileReviewed || !canReview || saving || !profileId}
          className="flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40"
          title={!aa?.canMarkBusinessProfileReviewed ? "Позначити перевіреним може керівник" : !canReview ? "Заповніть обов'язкові поля гілки" : undefined}
        >
          {saving && saveMutation.variables === "save_and_mark_reviewed" ? <Loader2 className="h-4 w-4 animate-spin" /> : aa?.canMarkBusinessProfileReviewed ? <ClipboardCheck className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
          Позначити перевіреним
        </button>
      </div>
    </div>
  );
}

/* ── breeds picker (filters by selected animalTypes) ─────────────────────── */
function BreedsPicker({
  animalTypes,
  selected,
  onChange,
}: {
  animalTypes: string[];
  selected: BreedRef[];
  onChange: (b: BreedRef[]) => void;
}) {
  const wantDog = animalTypes.includes("dogs");
  const wantCat = animalTypes.includes("cats");

  const dogQ = useQuery({ queryKey: ["breeds", "dog"], queryFn: ({ signal }) => fetchBreeds("dog", signal), enabled: wantDog, staleTime: 600_000 });
  const catQ = useQuery({ queryKey: ["breeds", "cat"], queryFn: ({ signal }) => fetchBreeds("cat", signal), enabled: wantCat, staleTime: 600_000 });

  const all: BreedRef[] = [
    ...(wantDog ? dogQ.data?.items ?? [] : []),
    ...(wantCat ? catQ.data?.items ?? [] : []),
  ];
  const selIds = new Set(selected.map((b) => b.id));

  if (animalTypes.length === 0) {
    return <p className="mt-3 text-xs text-muted-foreground">Оберіть види тварин, щоб завантажити породи.</p>;
  }

  return (
    <Field label="Породи (з довідника)" className="mt-3">
      {(dogQ.isPending && wantDog) || (catQ.isPending && wantCat) ? (
        <div className="h-9 animate-pulse rounded bg-muted" />
      ) : (
        <div className="max-h-48 overflow-y-auto rounded-md border p-2">
          <div className="flex flex-wrap gap-1.5">
            {all.map((b) => (
              <Chip
                key={b.id}
                on={selIds.has(b.id)}
                onClick={() =>
                  onChange(selIds.has(b.id) ? selected.filter((x) => x.id !== b.id) : [...selected, b])
                }
              >
                {b.nameUa}
              </Chip>
            ))}
            {all.length === 0 && <span className="text-xs text-muted-foreground">Порід не знайдено.</span>}
          </div>
        </div>
      )}
    </Field>
  );
}

/* ── multi-select chips (multiEnum) ──────────────────────────────────────── */
export function ChipMulti({
  options,
  selected,
  onToggle,
  scroll,
}: {
  options: Array<[string, string]>;
  selected: string[];
  onToggle: (v: string) => void;
  scroll?: boolean;
}) {
  const body = (
    <div className="flex flex-wrap gap-1.5">
      {options.map(([k, label]) => (
        <Chip key={k} on={selected.includes(k)} onClick={() => onToggle(k)}>{label}</Chip>
      ))}
    </div>
  );
  return scroll ? <div className="max-h-40 overflow-y-auto rounded-md border p-2">{body}</div> : body;
}

/* ── brands picker (KormaxBrand catalog; resolves inactive saved refs) ────── */
export function BrandsPicker({ selected, onChange }: { selected: BrandRef[]; onChange: (b: BrandRef[]) => void }) {
  const bq = useQuery({ queryKey: ["brands"], queryFn: ({ signal }) => fetchBrands(undefined, signal), staleTime: 600_000 });
  const active = bq.data?.items ?? [];
  const activeIds = new Set(active.map((b) => b.biBrandId));
  const selIds = new Set(selected.map((b) => b.biBrandId));
  // active catalog + any saved-but-now-inactive brand (rendered, flagged inactive)
  const extra = selected.filter((b) => !activeIds.has(b.biBrandId)).map((b) => ({ ...b, inactive: true }));
  const chips: BrandRef[] = [...active, ...extra];

  return (
    <Field label="Представлені бренди Kormax" req hint="Каталог брендів Kormax" className="mt-3">
      {bq.isPending ? (
        <div className="h-9 animate-pulse rounded bg-muted" />
      ) : (
        <div className="max-h-48 overflow-y-auto rounded-md border p-2">
          <div className="flex flex-wrap gap-1.5">
            {chips.map((b) => (
              <Chip
                key={b.biBrandId}
                on={selIds.has(b.biBrandId)}
                onClick={() =>
                  onChange(
                    selIds.has(b.biBrandId)
                      ? selected.filter((x) => x.biBrandId !== b.biBrandId)
                      : [...selected, { biBrandId: b.biBrandId, name: b.name }],
                  )
                }
              >
                {b.name}{b.inactive ? " (неактивний)" : ""}
              </Chip>
            ))}
            {chips.length === 0 && <span className="text-xs text-muted-foreground">Брендів не знайдено.</span>}
          </div>
        </div>
      )}
    </Field>
  );
}

/* ── primitives ──────────────────────────────────────────────────────────── */
function Card({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="mb-2">
        <div className="text-sm font-semibold">{title}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
      {children}
    </div>
  );
}
export function Field({ label, req, hint, className, children }: { label: string; req?: boolean; hint?: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <div className="mb-1 text-xs font-medium text-muted-foreground">
        {label}{req && <span className="ml-0.5 text-warning-foreground">*</span>}
      </div>
      {children}
      {hint && <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}
export function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: Array<[string, string]> }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
export function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
        on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}
